import { useCallback, useEffect, useRef, useState } from 'react';
import { DragIcon } from '../icons';

const VIDEO_SRC = '/loading_warmup.mp4';
const PX_PER_FULL_TURN = 520;
const IDLE_PLAYBACK_RATE = 0.4;

export function HeroStage() {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const dragRef = useRef({ active: false, lastX: 0, moved: false });
  const [duration, setDuration] = useState(0);
  const [angle, setAngle] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [hintGone, setHintGone] = useState(false);

  const updateAngleFromVideo = useCallback(() => {
    const v = videoRef.current;
    if (!v || !v.duration || !Number.isFinite(v.duration)) return;
    const next = ((v.currentTime / v.duration) * 360) % 360;
    setAngle(next < 0 ? next + 360 : next);
  }, []);

  // Idle playback drives the auto turntable. Restarts when not dragging.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    let raf = 0;
    const tick = () => {
      raf = requestAnimationFrame(tick);
      updateAngleFromVideo();
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [updateAngleFromVideo]);

  const startIdle = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    v.playbackRate = IDLE_PLAYBACK_RATE;
    const p = v.play();
    if (p && typeof p.catch === 'function') p.catch(() => {});
  }, []);

  const onLoadedMetadata = () => {
    const v = videoRef.current;
    if (!v) return;
    setDuration(v.duration || 0);
    startIdle();
  };

  const seekBy = (deltaX: number) => {
    const v = videoRef.current;
    if (!v || !duration) return;
    const dt = (deltaX / PX_PER_FULL_TURN) * duration;
    let next = (v.currentTime + dt) % duration;
    if (next < 0) next += duration;
    v.currentTime = next;
    updateAngleFromVideo();
  };

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    const v = videoRef.current;
    if (!v) return;
    (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
    dragRef.current = { active: true, lastX: e.clientX, moved: false };
    setDragging(true);
    v.pause();
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current.active) return;
    const dx = e.clientX - dragRef.current.lastX;
    dragRef.current.lastX = e.clientX;
    if (!dragRef.current.moved && Math.abs(dx) > 1) {
      dragRef.current.moved = true;
      setHintGone(true);
    }
    seekBy(dx);
  };

  const endDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current.active) return;
    dragRef.current.active = false;
    setDragging(false);
    try {
      (e.currentTarget as HTMLDivElement).releasePointerCapture(e.pointerId);
    } catch {
      /* no-op */
    }
    startIdle();
  };

  const displayDeg = String(Math.round(angle)).padStart(3, '0');

  return (
    <div
      className="ticks grain"
      style={{
        position: 'relative',
        aspectRatio: '1 / 1.12',
        borderRadius: 4,
        border: '0.5px solid var(--line-2)',
        overflow: 'hidden',
        background: '#07080a',
        cursor: dragging ? 'grabbing' : 'grab',
        touchAction: 'none',
        userSelect: 'none',
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
    >
      <span className="tick-br"></span>
      <video
        ref={videoRef}
        src={VIDEO_SRC}
        muted
        playsInline
        loop
        preload="auto"
        autoPlay
        onLoadedMetadata={onLoadedMetadata}
        onCanPlay={startIdle}
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />

      <div
        style={{
          position: 'absolute',
          top: 16,
          left: 16,
          display: 'flex',
          gap: 8,
          zIndex: 4,
          fontFamily: 'var(--font-mono)',
          fontSize: 9.5,
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          color: 'var(--fg-3)',
        }}
      >
        <span>Classical</span>
        <span style={{ color: 'var(--fg-4)' }}>·</span>
        <span>Turntable</span>
      </div>
      <div
        className="tnum"
        style={{
          position: 'absolute',
          top: 16,
          right: 18,
          zIndex: 4,
          fontFamily: 'var(--font-mono)',
          fontSize: 9.5,
          letterSpacing: '0.08em',
          color: 'var(--fg-3)',
        }}
      >
        {displayDeg}°
      </div>
      <div
        style={{
          position: 'absolute',
          bottom: 16,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 4,
          display: 'flex',
          alignItems: 'center',
          gap: 7,
          color: 'var(--fg-3)',
          fontFamily: 'var(--font-mono)',
          fontSize: 10,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          padding: '7px 13px',
          borderRadius: 999,
          background: 'rgba(8,8,10,0.5)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          border: '0.5px solid var(--line-1)',
          opacity: hintGone ? 0 : 1,
          transition: 'opacity .4s',
          pointerEvents: 'none',
        }}
      >
        <DragIcon /> Drag to turn
      </div>
    </div>
  );
}
